import CustomDropdown from '../reusable/dropdown';
import LabelWrapper from '../wrapper/label';
import { PipelineObject } from '../../params/pipeline';
import { SelectChangeEvent } from '@mui/material';

/**
 * @heading1: Select Pipeline Component
 * @paragraph: This component is used to select a pipeline from a list of available pipelines.
 * It provides a dropdown interface with an optional label and mandatory field indication.
 * @interface: SelectPipelineProps
 * @property: {string} label - The label for the dropdown.
 * @property: {boolean} isMandatory - Indicates whether the field is mandatory.
 * @property: {PipelineObject[]} pipelines - Array of pipelines to display in the dropdown.
 * @property: {(event: SelectChangeEvent<unknown>) => void} onChange - Callback function triggered when the value changes.
 * @property: {boolean} error - Indicates if there is a validation error for the field.
 * @property: {string} value - The current value of the dropdown.
 */
interface SelectPipelineProps {
    label?: string;
    isMandatory?: boolean;
    pipelines?: PipelineObject[];
    onChange?: (event: SelectChangeEvent<unknown>) => void;
    error?: boolean;
    value?: string | number;
    disabled?: boolean;
    [key: string]: unknown;
}

/**
 * @heading1: SelectPipeline
 * @paragraph: A reusable component that renders a labeled dropdown for selecting a pipeline.
 * It supports mandatory field indication and validation error handling.
 * @param: {SelectPipelineProps} props - The props required for this component.
 * @returns: {JSX.Element} - The rendered SelectPipeline component.
 */
const SelectPipeline: React.FC<SelectPipelineProps> = ({ label = '', isMandatory = false, pipelines = [], disabled = false, onChange = () => null, error = false, value = '', ...props }: SelectPipelineProps) => {
    return <>
        {/* Renders the label with an optional mandatory indicator */}
        {label && <LabelWrapper label={label} isMandatory={isMandatory} />}
        {/* Dropdown for selecting a pipeline */}
        <CustomDropdown
            options={pipelines.map((pipe: PipelineObject) => ({
                label: pipe.name,
                value: pipe.id
            }))}
            onChange={onChange}
            error={error}
            value={value}
            disabled={disabled}
            {...props}
        />
    </>;
};

export default SelectPipeline;